/**
 * DocuParser is host-direct like Deckorate (see
 * integrations/deckorate/services/deckorateApi.ts): it has no per-connector
 * Cognito login/logout of its own, so the client is built from a plain
 * authorized-fetch function (the add-in's own AIVA session token attached by
 * the caller — see hooks/useDocuparserHttp.ts) instead of the Cognito
 * ServiceFetch(key, init) used by MyQueue/LogoCloud/Transcribe/Engage.
 *
 * The wire contract here mirrors the real backend at
 * apps/backend/nonai/aiva-studio-nonai-backend/Api/DocuParser.Endpoints
 * (AIVA-7127) — routes are under `/api/documents/...`, and every response is
 * wrapped in that project's `ApiResponse<T>` envelope (`{ success, data,
 * error, correlationId }`), not the `{ message, detailedMessages,
 * responseType }` shape some other DocuParser backend prototype used.
 */
export type DocuparserAuthorizedFetch = (url: string, init?: RequestInit) => Promise<Response>;

/* ── Domain types (mirrors DocuParser API payload shape) ── */

export interface DocuparserDocument {
  id: number;
  name: string;
  description: string;
  status: string;
  createdBy: string;
  createdAt: string;
}

export interface DocumentSearchData {
  totalRecords: number;
  searchResultHeading: string;
  results: DocuparserDocument[];
}

export interface DocumentDetail extends DocuparserDocument {
  inputFileName: string;
  inputPresignedUrl: string;
  outputFileName: string;
  outputPresignedUrl: string;
}

export interface InitiateUploadData {
  presignedUrl: string;
  inputBlobPath: string;
}

/** Uniform wire envelope every DocuParser.Endpoints action returns. */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  correlationId?: string;
}

export type DocumentSearchResponse = ApiResponse<DocumentSearchData>;
export type DocumentDetailResponse = ApiResponse<DocumentDetail>;
export type InitiateUploadResponse = ApiResponse<InitiateUploadData>;
export type CompleteUploadResponse = ApiResponse<unknown>;

interface JsonRequest {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
}

/* ── Client factory ──────────────────────────────── */

function buildUrl(base: string, path: string): string {
  if (!base) {
    throw new Error("DocuParser API base URL is not configured on the connection (apiBaseUrl).");
  }
  return base.replace(/\/$/, "") + "/api" + path;
}

export interface DocuparserClient {
  searchDocuments(keyword: string, pageNumber: number, pageSize: number): Promise<DocumentSearchResponse>;
  getDocumentDetails(documentId: number): Promise<DocumentDetailResponse>;
  initiateUpload(filename: string): Promise<InitiateUploadResponse>;
  completeUpload(
    filename: string,
    name: string,
    description: string,
    inputBlobPath: string,
  ): Promise<CompleteUploadResponse>;
}

export function createDocuparserClient(
  authorizedFetch: DocuparserAuthorizedFetch,
  apiBaseUrl: string,
): DocuparserClient {
  async function jsonCall<T>(path: string, init: JsonRequest = {}): Promise<ApiResponse<T>> {
    const method = init.method ?? "GET";
    const headers: Record<string, string> = {};
    let body: BodyInit | undefined;
    if (init.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(init.body);
    }
    const res = await authorizedFetch(buildUrl(apiBaseUrl, path), {
      method,
      headers,
      body,
    });
    const text = await res.text();
    const parsed = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const message = parsed?.error ?? `DocuParser ${method} ${path}: ${res.status}`;
      const error = new Error(message) as Error & { status?: number; correlationId?: string };
      error.status = res.status;
      error.correlationId = parsed?.correlationId;
      throw error;
    }
    return parsed as ApiResponse<T>;
  }

  return {
    searchDocuments: (keyword, pageNumber, pageSize) =>
      jsonCall<DocumentSearchData>("/documents/search", {
        method: "POST",
        body: { keyword: keyword.trim(), pageNumber, pageSize },
      }),

    getDocumentDetails: (documentId) => jsonCall<DocumentDetail>(`/documents/${documentId}`),

    initiateUpload: (filename) =>
      jsonCall<InitiateUploadData>("/documents/initiate-upload", {
        method: "POST",
        body: { fileName: filename.trim() },
      }),

    completeUpload: (filename, name, description, inputBlobPath) =>
      jsonCall<unknown>("/documents/upload", {
        method: "POST",
        body: {
          fileName: filename.trim(),
          name: name.trim(),
          description: description.trim(),
          inputBlobPath,
        },
      }),
  };
}
